import { createContext, useContext, useState, useCallback, useMemo } from 'react';

export interface HoverCardContextValue {
  hoverOnly: boolean;
  setOpen: (open: boolean) => void;
  open: boolean;
  handlePointerEnter: () => void;
  handlePointerLeave: () => void;
}

export const HoverCardContext = createContext<HoverCardContextValue | null>(null);

export function useHoverCardContext() {
  const context = useContext(HoverCardContext);
  return context;
}

export interface HoverCardContextProviderProps {
  hoverOnly: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

export function HoverCardContextProvider({
  hoverOnly,
  open: controlledOpen,
  onOpenChange,
  children,
}: HoverCardContextProviderProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const setOpen = useCallback(
    (newOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(newOpen);
      }

      onOpenChange?.(newOpen);
    },
    [isControlled, onOpenChange],
  );

  const handlePointerEnter = useCallback(() => {
    if (!hoverOnly) return;

    setOpen(true);
  }, [hoverOnly, setOpen]);

  const handlePointerLeave = useCallback(() => {
    if (!hoverOnly) return;

    setOpen(false);
  }, [hoverOnly, setOpen]);

  const contextValue = useMemo<HoverCardContextValue>(
    () => ({
      hoverOnly,
      setOpen,
      open,
      handlePointerEnter,
      handlePointerLeave,
    }),
    [hoverOnly, setOpen, open, handlePointerEnter, handlePointerLeave],
  );

  return <HoverCardContext.Provider value={contextValue}>{children}</HoverCardContext.Provider>;
}
